import { DagEntityCounts } from './InternalDagInterfaces';

/**
 * From analysis around recent historical data
 * https://app.hex.tech/fishtown/hex/7283942d-8f95-48e4-9215-e1f05aa23c30/draft/logic
 *
 * At the time of writing, this allowed ~83.1% of DAGs that took
 * longer than 30 ms to render unconditionally.
 * The p999999 was 37.7 sec for this score, with a p9999 of 2.3 sec, p99 of 381 ms.
 * If complexity is less than this value, feel free to render unconditionally.
 * Otherwise, we should warn a user and allow them to render it manually.
 *
 * This number should be between 0.0 and 1.0, where 0.0 would allow all dags to be rendered
 * and 1.0 would disallow all dags. The values are not evenly distributed (i.e. 83.1% of
 * dag renders are < 0.027777... at the time of writing).
 */
export const DEFAULT_COMPLEXITY_SCORE_THRESHOLD = 0.027777777;

/**
 * https://github.com/dagrejs/graphlib/wiki/API-Reference#alg-dijkstra-all
 * O(|V| * (|E| + |V|) * log |V|)
 */
const DJIKSTRA_ALL_MAX_VALUE = 333389900;

/**
 * https://github.com/dagrejs/graphlib/wiki/API-Reference#alg-floyd-warshall
 * O(|V|^3)
 */
const FLOYD_WARSHALL_MAX_VALUE = 63712430000;

/**
 * https://github.com/dagrejs/graphlib/wiki/API-Reference#alg-topsort
 */
const TOP_SORT_MAX_VALUE = 7429;

const clamp = (val: number, min = 0, max = 1) => Math.max(Math.min(val, max), min);

type ComplexityScores = {
  djikstraAllScore: number;
  floydWarshallScore: number;
  topSortScore: number;
};

/**
 * Looking at dagre's docs, we find a number of algorithms with different O()
 * complexities. The way we compute a score is we identified anecdata maximum values
 * for these literal computations, and normalize each of these 3 scores such
 * that each score will be on a scale from 0.0 to 1.0. We then return the max value
 * of these three measures of complexity, clamp the value between 0.0 and 1.0,
 * and return it. The Dag Complexity will always be a number from 0.0 to 1.0.
 */
const calculateDagComplexity = ({
  nodeCount,
  edgeCount,
}: DagEntityCounts): ComplexityScores => {
  const djikstraAllRawValue =
    nodeCount * (edgeCount + nodeCount) + Math.log10(nodeCount + 1);
  const floydWarshallRawValue = nodeCount ** 3;
  const topSortRawValue = nodeCount + edgeCount;

  const djikstraAllScore = clamp(djikstraAllRawValue / DJIKSTRA_ALL_MAX_VALUE);
  const floydWarshallScore = clamp(floydWarshallRawValue / FLOYD_WARSHALL_MAX_VALUE);
  const topSortScore = clamp(topSortRawValue / TOP_SORT_MAX_VALUE);
  return {
    djikstraAllScore,
    floydWarshallScore,
    topSortScore,
  };
};

const EPSILON = 0.000000001;

/**
 * A {@link complexityThresholds} of 0.0 would disallow all DAGs, 1.0 would allow all DAGs.
 */
export const shouldAutoRenderDag = (
  dagStats: DagEntityCounts,
  complexityThresholds: ComplexityScores,
) => {
  const complexity = calculateDagComplexity(dagStats);
  const { djikstraAllScore, floydWarshallScore, topSortScore } = complexity;

  return {
    complexity,
    shouldAutoRender:
      djikstraAllScore - EPSILON <= complexityThresholds.djikstraAllScore &&
      floydWarshallScore - EPSILON <= complexityThresholds.floydWarshallScore &&
      topSortScore - EPSILON <= complexityThresholds.topSortScore,
  };
};
